import { Helmet } from "react-helmet-async";
import { PageHero } from "@/components/shared/PageHero";
import { ServiceSidebar } from "@/components/services/ServiceSidebar";
import { CTASection } from "@/components/shared/CTASection";
import { ServiceFAQ } from "@/components/services/ServiceFAQ";
import { aerationFaqs } from "@/data/serviceFaqs";
import { Wind, Sprout, Droplets, Leaf, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import lawnImage from "@/assets/services/aeration-overseeding.jpg";

const features = [
  {
    icon: Wind,
    title: "Core Aeration",
    description: "Professional core aeration removes soil plugs to reduce compaction and improve root growth.",
  },
  {
    icon: Sprout,
    title: "Overseeding",
    description: "Premium grass seed fills thin areas and introduces improved grass varieties to your lawn.",
  },
  {
    icon: Droplets,
    title: "Better Absorption",
    description: "Aeration allows water, air, and nutrients to reach the root zone more effectively.",
  },
  {
    icon: Leaf,
    title: "Dethatching",
    description: "Remove the layer of dead grass and debris that prevents healthy lawn growth.",
  },
];

const Aeration = () => {
  return (
    <>
      <Helmet>
        <title>Aeration & Overseeding | Riekels Lawn Care & Landscaping</title>
        <meta
          name="description"
          content="Professional lawn aeration and overseeding in the West Twin Cities Metro, MN. Improve your lawn's health with core aeration. Call (763) 245-7813."
        />
      </Helmet>
      <main>
        <PageHero
          badge="Our Services"
          title="Aeration & Overseeding"
          subtitle="Rejuvenate your lawn with professional aeration and premium overseeding."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
              <div className="lg:col-span-1">
                <ServiceSidebar />
              </div>
              <div className="lg:col-span-3">
                <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={lawnImage} 
                    alt="Healthy aerated lawn" 
                    className="w-full h-64 md:h-80 object-cover object-center"
                  />
                </div>

                <div className="prose prose-lg max-w-none mb-12">
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-6">
                    Breathe New Life Into Your Lawn
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Over time, soil becomes compacted from foot traffic, mowing, and natural settling. 
                    Compacted soil prevents water, air, and nutrients from reaching grass roots, leading to a thin, 
                    struggling lawn.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Core aeration is the solution. By removing small plugs of soil, we create channels that allow 
                    your lawn to breathe. Combined with overseeding, aeration is one of the most effective ways 
                    to improve lawn health and thickness.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {features.map((feature, index) => (
                    <article key={index} className="feature-card">
                      <div className="icon-circle mb-4">
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </article>
                  ))}
                </div>

                <div className="mt-12 bg-secondary rounded-2xl p-8 text-center">
                  <Wind className="w-12 h-12 mx-auto mb-4 text-secondary-foreground" />
                  <h3 className="text-2xl font-heading font-bold text-secondary-foreground mb-3">
                    Schedule Fall Aeration
                  </h3>
                  <p className="text-secondary-foreground/80 mb-6 max-w-lg mx-auto">
                    Fall is the ideal time for aeration in Minnesota. Book your service now.
                  </p>
                  <Link to="/contact">
                    <Button size="lg" className="bg-white hover:bg-white/90 text-secondary font-semibold">
                      Request a Quote <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={aerationFaqs} />

        <CTASection />
      </main>
    </>
  );
};

export default Aeration;
